// [PRD-2043] Shared types + formatters for the developer billing dashboard.
// These mirror the billing-service read DTOs proxied through the monolith.

export interface BsCustomer {
  id: string;
  monolithCustomerId: string;
  stripeCustomerId: string | null;
  name: string;
  email: string;
  status: string;
  chargeDay?: number;
  isPrepaid?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface BsSubscription {
  id: string;
  customerId: string;
  planName: string;
  status: string;
  amountCents: number;
  currency: string;
  billingInterval: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  nextBillingDate: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  createdAt: string;
}

export interface BsInvoiceLineItem {
  id: string;
  invoiceId: string;
  type: string;
  description: string;
  amountCents: number;
  quantity: number;
}

// --- [PRD-3321] Manual charge approval gate ---

export interface BsChargeApproval {
  id: string;
  invoiceId: string;
  customerId: string;
  customerName: string | null;
  customerMonolithId: string | null;
  subscriptionId: string | null;
  amountCents: number;
  currency: string;
  status: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BsGuardWarning {
  recentChargeId: string;
  recentChargeAmountCents: number;
  recentChargeStatus: string;
  recentChargeCreatedAt: string;
  windowDays: number;
}

// Per-employee payroll breakdown on an "employee_cost" line item — cents.
export interface BsPayrollBreakdown {
  employeeId?: string;
  salary?: number;
  platformFee?: number;
  bonus?: number;
  raise?: number;
  discount?: number;
}

export interface BsChargeApprovalLineItem extends BsInvoiceLineItem {
  breakdown: BsPayrollBreakdown | null;
}

export interface BsChargeApprovalDetail {
  approval: BsChargeApproval;
  invoiceStatus: string;
  lineItems: BsChargeApprovalLineItem[];
  guardWarning: BsGuardWarning | null;
}

export interface BsInvoice {
  id: string;
  customerId: string;
  subscriptionId: string | null;
  type: string;
  status: string;
  totalAmountCents: number;
  currency: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  dueDate: string;
  paidAt: string | null;
  voidedAt: string | null;
  /** [PRD-3348] billing-service invoice metadata, e.g. `voidReason` on voided invoices. */
  metadata?: Record<string, unknown> | null;
  lineItems?: BsInvoiceLineItem[];
  createdAt: string;
}

export interface BsCharge {
  id: string;
  invoiceId: string;
  customerId: string;
  amountCents: number;
  currency: string;
  status: string;
  stripePaymentIntentId: string | null;
  failureReason: string | null;
  attemptNumber: number;
  createdAt: string;
  /** [PRD-3765] Stripe charge time for migrated history; null for charges made in billing-service. */
  chargedAt?: string | null;
  // Enriched by BS's charge list endpoint (leftJoin on customers/payment_methods).
  customerName?: string | null;
  customerEmail?: string | null;
  monolithCustomerId?: string | null;
  paymentType?: string | null;
  /** [PRD-3765] List endpoint only: the invoice's due date and its number of attempts. */
  invoiceDueDate?: string | null;
  attemptCount?: number;
}

// [PRD-2043] Day/status rollup row for the Charge Report summary table.
export interface BsChargeSummaryRow {
  day: string;
  status: string;
  charges: number;
  customers: number;
  amountCents: number;
}

export interface BsRefund {
  id: string;
  chargeId: string;
  invoiceId: string;
  customerId: string;
  amountCents: number;
  currency: string;
  status: string;
  reason: string | null;
  gatewayRefundId: string | null;
  failureReason: string | null;
  createdAt: string;
}

export interface BsPaymentMethod {
  id: string;
  type: string;
  isDefault: boolean;
  lastFour: string | null;
  brand: string | null;
  bankName: string | null;
  status: string;
  // ACH mandates are stored here as { mandate_id: "mandate_..." }.
  metadata: Record<string, unknown> | null;
}

/** Cents (integer) → "$1,234.56". */
export const formatMoney = (cents: number | null | undefined, currency = "usd"): string => {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format(cents / 100);
};

/** A 0–1 ratio → "72.5%". */
export const formatPercent = (ratio: number | null | undefined, digits = 1): string => {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return "—";
  return `${(ratio * 100).toFixed(digits)}%`;
};

/** A value already expressed as a percentage (0–100) → "77.8%". */
export const formatRate = (value: number | null | undefined, digits = 1): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${Number(value).toFixed(digits)}%`;
};

/**
 * ISO date → "Aug 7, 2026". Rendered in UTC: the billing service sends calendar
 * dates as UTC midnight ("2026-10-01T00:00:00.000Z") or bare "YYYY-MM-DD", so
 * formatting in the viewer's local zone would show the previous day in the US.
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
};

// [PRD-3767] Billing service owns the prepaid/postpaid flag and the charge day.

/** Day of month → "1st", "2nd", "11th", "15th", "23rd". "—" when unknown. */
export const formatOrdinalDay = (day: number | null | undefined): string => {
  if (typeof day !== "number" || !Number.isInteger(day) || day < 1) return "—";
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
  const suffix = ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[day % 10] || "th";
  return `${day}${suffix}`;
};

/** [PRD-3767] Plain English billing schedule for the customer header. "—" when either value is unknown. */
export const formatBillingSchedule = (
  isPrepaid: boolean | null | undefined,
  chargeDay: number | null | undefined,
): string => {
  const day = formatOrdinalDay(chargeDay);
  if (typeof isPrepaid !== "boolean" || day === "—") return "—";
  return isPrepaid
    ? `Charged in advance on the ${day} for the upcoming period`
    : `Charged in arrears on the ${day} for the previous period`;
};

/**
 * [PRD-3767] "Oct 15, 2026 for Oct 15, 2026 to Nov 15, 2026". BS sets
 * nextBillingDate as the charge date for the subscription's current
 * billingPeriodStart..billingPeriodEnd, so the two belong together. Uses the
 * active subscription, else a paused one; "—" when none has a next date.
 */
export const formatNextCharge = (subscriptions: BsSubscription[]): string => {
  const sub =
    subscriptions.find((s) => s.status === "active") ?? subscriptions.find((s) => s.status === "paused");
  if (!sub?.nextBillingDate) return "—";
  return `${formatDate(sub.nextBillingDate)} for ${formatDate(sub.billingPeriodStart)} to ${formatDate(
    sub.billingPeriodEnd,
  )}`;
};

/**
 * Maps a status string to a CustomChip semantic color (light-tinted):
 * success = green, error = red, warning = amber, default = gray.
 */
export const statusColor = (status: string | null | undefined): string => {
  const s = String(status || "").toLowerCase();
  if (["paid", "succeeded", "active", "balanced", "resolved", "completed", "approved"].includes(s)) return "success";
  if (
    [
      "failed",
      "void",
      "canceled",
      "cancelled",
      "uncollectible",
      "discrepancy_found",
      "disputed",
      "chargeback",
      "rejected",
    ].includes(s)
  ) {
    return "error";
  }
  if (
    [
      "pending",
      "draft",
      // [PRD-3767] finalized = issued but unpaid invoice in BS.
      "finalized",
      "processing",
      "past_due",
      "unpaid",
      "open",
      "scheduled",
      "requires_payment_method",
      "requires_action",
      "paused",
    ].includes(s)
  ) {
    return "warning";
  }
  return "default";
};

/** Title-case a status token, e.g. "requires_payment" → "Requires Payment". */
export const titleCase = (value: string | null | undefined): string => {
  if (!value) return "—";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Map a BS payment-method type to the label used in the derived CSV (ACH / Card). */
export const paymentTypeLabel = (type: string | null | undefined): string => {
  if (!type) return "—";
  const t = type.toLowerCase();
  if (t === "bank_account" || t === "ach" || t === "us_bank_account") return "ACH";
  if (t === "card" || t === "credit_card") return "Card";
  if (t === "cheque" || t === "check") return "Cheque";
  return titleCase(type);
};

// [PRD-3765] Short, plain-language version of a gateway failure reason for the retry
// snackbar. The full text stays in the Failure column (click to expand).
const FAILURE_SUMMARIES: { match: RegExp; text: string }[] = [
  {
    match: /previously used with a PaymentIntent|detached from a Customer|No such PaymentMethod/i,
    text: "The customer's payment method can no longer be used. Ask them to add a new one.",
  },
  { match: /no default payment method|no payment method/i, text: "The customer has no payment method on file." },
  { match: /insufficient[_ ]funds/i, text: "Insufficient funds." },
  { match: /expired[_ ]card|card has expired/i, text: "The card has expired." },
  { match: /card[_ ]declined|card was declined|do_not_honor/i, text: "The card was declined." },
  { match: /mandate/i, text: "The bank account mandate is not valid. Ask the customer to re-authorize it." },
  { match: /timeout|circuit breaker|gateway unavailable/i, text: "Stripe was unavailable. Try again in a few minutes." },
];

export const summarizeFailureReason = (reason: string | null | undefined): string => {
  const raw = (reason ?? "").trim();
  if (!raw) return "No reason was given.";
  const known = FAILURE_SUMMARIES.find((s) => s.match.test(raw));
  if (known) return known.text;
  // Unknown: drop billing-service's "Payment operation failed:" style prefix, keep the first sentence.
  const text = raw.replace(/^(Payment (operation failed|gateway error):\s*)+/i, "");
  const first = text.split(/(?<=\.)\s/)[0];
  return first.length > 140 ? `${first.slice(0, 137)}…` : first;
};

// [PRD-3791] Filter options for the dashboard tabs. Values match billing-service's
// accepted query values; "" means no filter.
export const CUSTOMER_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "disabled", label: "Disabled" },
  { value: "churned", label: "Churned" },
  { value: "new", label: "New" },
];

export const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "canceled", label: "Canceled" },
  { value: "past_due", label: "Past due" },
];

export const INVOICE_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "finalized", label: "Finalized" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Void" },
];

export const INVOICE_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "recurring", label: "Recurring" },
  { value: "one_time", label: "One-time" },
  { value: "onboarding", label: "Onboarding" },
];

export const CHARGE_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
];

export const REFUND_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
];

export const DISPUTE_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "investigating", label: "Investigating" },
  { value: "resolved", label: "Resolved" },
  { value: "dismissed", label: "Dismissed" },
];

// [PRD-3791] Migrated records are saved on the migration day, so their created_at is
// "when it moved to billing-service", not a signup or billing date.
export const ON_BS_SINCE_LABEL = "On billing-service since";

// --- [PRD-2043] Expected Funds (collections forecast) ---

export type ExpectedFundsTier = "settled" | "inTransit" | "pending" | "projected";

export interface ExpectedFundsBreakdown {
  settledCents: number;
  inTransitCents: number;
  pendingCents: number;
  projectedCents: number;
}

export interface ExpectedFundsRow {
  type: "day" | "gap";
  // day rows
  date?: string;
  isToday?: boolean;
  confirmed?: boolean;
  estimated?: boolean;
  breakdown?: ExpectedFundsBreakdown;
  amountCents?: number; // still to land (in-transit + pending + projected)
  receivedCents?: number; // already settled on this date
  chargeCount?: number;
  // gap rows
  start?: string;
  end?: string;
  reason?: string;
}

export interface ExpectedFundsResponse {
  syncedAt: string;
  today: string;
  range: { start: string; end: string; lookBackDays: number; lookAheadDays: number };
  totals: ExpectedFundsBreakdown & { upcomingTotalCents: number; arrivedCents: number; grandTotalCents: number };
  rows: ExpectedFundsRow[];
}

export interface ExpectedFundsChargePaymentMethod {
  type: string | null;
  brand: string | null;
  bankName: string | null;
  lastFour: string | null;
}

export interface ExpectedFundsChargeRow {
  customerName: string | null;
  stripeCustomerId: string | null;
  amountCents: number;
  paymentMethod: ExpectedFundsChargePaymentMethod;
  subscription: { planName: string | null; seats: number | null };
  chargeDate: string | null;
  tier: ExpectedFundsTier;
}

export interface ExpectedFundsChargesResponse {
  date: string;
  isEstimated: boolean;
  chargeCount: number;
  totalCents: number;
  receivedCents: number;
  breakdown: ExpectedFundsBreakdown;
  rows: ExpectedFundsChargeRow[];
}

/**
 * Tier presentation — each tier is distinguished by a glyph AND a fill pattern
 * (SCSS `ef-fill-*`), never colour alone (accessibility + the PRD acceptance
 * criteria). `key` doubles as the SCSS modifier suffix.
 */
export const EXPECTED_FUNDS_TIERS: {
  key: ExpectedFundsTier;
  label: string;
  glyph: string;
  amountKey: keyof ExpectedFundsBreakdown;
  description: string;
}[] = [
  { key: "settled", label: "Settled", glyph: "✓", amountKey: "settledCents", description: "Already in the operating account" },
  { key: "inTransit", label: "In transit", glyph: "↗", amountKey: "inTransitCents", description: "Charge succeeded, in balance or paying out" },
  { key: "pending", label: "Pending", glyph: "◆", amountKey: "pendingCents", description: "ACH initiated, not yet settled" },
  { key: "projected", label: "Projected", glyph: "○", amountKey: "projectedCents", description: "Not charged yet, scheduled to fire" },
];

const parseCalendarDate = (iso: string): Date => new Date(`${iso}T00:00:00`);

/** "2026-10-02" → "Fri 2 Oct" (or "Fri 2 Oct 2026" with the year). */
export const formatBankDate = (iso: string | null | undefined, withYear = false): string => {
  if (!iso) return "—";
  const d = parseCalendarDate(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
  const month = d.toLocaleDateString("en-US", { month: "short" });
  return withYear ? `${weekday} ${d.getDate()} ${month} ${d.getFullYear()}` : `${weekday} ${d.getDate()} ${month}`;
};

/** Relative phrasing vs today: "today", "tomorrow", "yesterday", "in 5 days", "3 days ago". */
export const formatRelativeDay = (iso: string | null | undefined, todayIso: string): string => {
  if (!iso) return "";
  const days = Math.round((parseCalendarDate(iso).getTime() - parseCalendarDate(todayIso).getTime()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
};

/** ISO instant → "6:12 AM ET" (US Eastern), for the "Synced …" label. */
export const formatSyncedTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET`;
};

/** Cents → "18,970" (no symbol, no cents) for the compact in-bar breakdown labels. */
export const formatAmountShort = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return "—";
  return Math.round(cents / 100).toLocaleString("en-US");
};

/** A charge's payment method as a display string: "Mastercard ··1181" / "ACH · Wells ··8802". */
export const formatPaymentMethod = (pm: ExpectedFundsChargePaymentMethod | null | undefined): string => {
  if (!pm || !pm.type) return "—";
  const last4 = pm.lastFour ? `··${pm.lastFour}` : "";
  const t = pm.type.toLowerCase();
  if (t === "card" || t === "credit_card") {
    return [pm.brand ? titleCase(pm.brand) : "Card", last4].filter(Boolean).join(" ");
  }
  return ["ACH", pm.bankName ? `· ${pm.bankName}` : "", last4].filter(Boolean).join(" ");
};

// --- [PRD-2625] Overview tab ---

export interface OverviewCountAmount {
  count: number;
  amountCents: number;
}

export interface OverviewReport {
  range: { startDate: string; endDate: string };
  syncedAt: string;
  currency: string;
  tiles: {
    collectedCents: number;
    outstandingCents: number;
    expectedNext30Cents: number;
    paymentSuccessRate: number;
  };
  collectionsOverTime: Array<{
    date: string;
    amountCents: number;
    chargeCount: number;
    successRate: number;
  }>;
  chargesByStatus: {
    succeeded: OverviewCountAmount;
    pending: OverviewCountAmount;
    failed: OverviewCountAmount;
  };
  failedCharges: Array<{
    customerName: string | null;
    customerId: string;
    amountCents: number;
    daysOverdue: number;
    reason: string;
  }>;
  subscriptionsByState: {
    active: { count: number; mrrCents: number };
    paused: { count: number; mrrCents: number };
    cancelled30d: { count: number; mrrCents: number };
  };
  credits: {
    toBeAppliedCents: number;
    unappliedCount: number;
    oldestUnappliedDays: number | null;
    recentlyIssued: Array<{
      customerName: string | null;
      amountCents: number;
      issuedAt: string;
      applied: boolean;
      appliedInvoiceDueDate: string | null;
    }>;
    refundsIssuedCents: number;
  };
  topCustomers: Array<{
    customerName: string | null;
    planName: string | null;
    seats: number | null;
    billedCents: number;
    pctOfMrr: number;
  }>;
  activeMrrCents: number;
}
